import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from '@g99-product-web-ui/shared-service';

import { MultifactorAuthenticationService } from '../../services/multifactor-authentication.service';

@Component({
  selector: 'g99-totp-setup',
  templateUrl: './totp-setup.component.html',
  styleUrl: './totp-setup.component.scss',
  standalone: false,
})
export class TotpSetupComponent {
  @Input() public accessToken!: string;
  @Input() public otpAuthUrl!: string;
  @Output() public emitChangeAuthMethod = new EventEmitter<void>();

  public otpForm: FormGroup;
  public errorMessage!: string;
  public isLoading!: boolean;
  protected errorMessages = {
    totpCode: {
      required: 'Verification code is required',
    },
  };

  constructor(
    private readonly _fb: FormBuilder,
    private readonly _mfaSetupService: MultifactorAuthenticationService,
    private readonly _router: Router,
    private readonly _toastService: ToastService
  ) {
    this.otpForm = this._fb.group({
      totpCode: ['', Validators.required],
    });
  }

  /**
   * Emits an event to navigate to the authentication method selection page.
   */
  public navigateToAuthSelectionPage(): void {
    this.emitChangeAuthMethod.emit();
  }

  /**
   * Logs out the user by clearing the local storage and redirects to the login page.
   */
  public logoutAndLogin(): void {
    localStorage.clear();
    this._router.navigate(['/']);
  }

  /**
   * Verifies the OTP entered by the user during the MFA setup.
   * If the form is valid, the TOTP code is sent for verification.
   *
   * @throws {Error} If the form is invalid or verification fails.
   */
  public onVerify(): void {
    if (this.otpForm.invalid) {
      return;
    }
    this.isLoading = true;
    this._mfaSetupService
      .verifyTotpCode(this.otpForm.value.totpCode, this.accessToken)
      .subscribe({
        next: () => this._handleVerificationSuccess(),
        error: (error: { message: string }) =>
          this._handleVerificationError(error),
      });
  }

  /**
   * Checks if a form control is invalid.
   * Used for showing error messages for specific fields in the form.
   *
   * @param controlName - Name of the form control to validate.
   * @returns `true` if the control is invalid and has been touched; otherwise, `false`.
   */
  protected isInvalid(controlName: string): boolean {
    const control = this.f[controlName];
    return !!control && control.touched && !!control.errors?.['required'];
  }

  /**
   * Getter for accessing form controls in the template.
   *
   * @returns {FormGroup} The form control group.
   */
  protected get f() {
    return this.otpForm.controls;
  }

  /**
   * Handles the successful OTP verification by clearing local storage and redirecting the user.
   */
  private _handleVerificationSuccess(): void {
    this.isLoading = false;
    this._router.navigate(['business/users']);
    this._toastService.showSuccess(
      'Your MFA setup is completed successfully. You will be redirected shortly.'
    );
  }

  /**
   * Handles OTP verification errors and displays an appropriate error message.
   *
   * @param {Error} error - The error object containing error details.
   */
  private _handleVerificationError(error: { message: string }): void {
    this.isLoading = false;
    this.errorMessage =
      error.message || 'Failed to set up TOTP. Please try again.';
    this._toastService.showError(this.errorMessage);
  }
}
